import { ChevronDownIcon } from "@xola/icons";
import { Badge, Checkbox } from "@xola/ui-kit";
import React, { useEffect, useRef, useState } from "react";
import { Permission } from "types";
import { MultiSelectPopover } from "@/components/MultiSelectPopover";

interface ProductOption {
    id: string;
    label: string;
    icon: React.ReactNode;
    category?: string;
    testId?: string;
    permission?: Permission;
}

interface FilterItemProps {
    label: string;
    isChecked: boolean;
    onChange: () => void;
    icon: React.ReactNode;
    testId?: string;
}

interface ProductFilterDropdownProps {
    options: ProductOption[];
    onSelectionChange: (selectedItems: string[]) => void;
    externalSelectedItems: string[];
}

const ProductFilterItem = ({ label, isChecked, onChange, icon, testId }: FilterItemProps) => (
    <MultiSelectPopover.Item className="border-b-dotted border-b border-gray-light bg-white">
        <div className="flex items-center">
            <Checkbox checked={isChecked} className="mr-2" data-testid={testId} onChange={onChange} />
            {icon}
            <span className="ml-2 text-sm font-bold text-black">{label}</span>
        </div>
    </MultiSelectPopover.Item>
);

export const ProductFilterDropdown = ({
    options,
    onSelectionChange,
    externalSelectedItems,
}: ProductFilterDropdownProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // tODO: refactor using lodash groupBy
    /* eslint-disable-next-line unicorn/no-array-reduce */
    const groupedOptions = options.reduce<Record<string, ProductOption[]>>((accumulator, option) => {
        const category = option.category ?? "default";
        if (!accumulator[category]) accumulator[category] = [];
        accumulator[category].push(option);
        return accumulator;
    }, {});

    const handleSelectAll = () => {
        const allSelected = externalSelectedItems.length === options.length;
        const newSelectedItems = allSelected ? [] : options.map((option) => option.id);
        onSelectionChange(newSelectedItems);
    };

    const handleItemChange = (id: string) => {
        const updatedSelection = externalSelectedItems.includes(id)
            ? externalSelectedItems.filter((item) => item !== id)
            : [...externalSelectedItems, id];
        onSelectionChange(updatedSelection);
    };

    let displayText = "";
    if (externalSelectedItems.length === 0 || externalSelectedItems.length === options.length) {
        displayText = "All";
    } else {
        displayText = options
            .filter((option) => externalSelectedItems.includes(option.id))
            .map((option) => option.label)
            .join(", ");
    }

    const toggleDropdownVisibility = () => {
        setIsVisible((previous) => !previous);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsVisible(false);
        }
    };

    useEffect(() => {
        if (isVisible) {
            window.addEventListener("click", handleClickOutside);
        } else {
            window.removeEventListener("click", handleClickOutside);
        }

        return () => window.removeEventListener("click", handleClickOutside);
    }, [isVisible]);

    return (
        <div ref={dropdownRef} className="relative w-100" data-testid="purchases-make-a-purchase-product-type-dropdown">
            <div
                className="flex cursor-pointer items-center rounded-md rounded-tl-md rounded-bl-md border border-gray bg-white px-4 py-2"
                onClick={toggleDropdownVisibility}
            >
                <span className="text-xs text-black">{displayText}</span>
                <span className="text-gray-500 ml-auto">
                    <ChevronDownIcon />
                </span>
            </div>

            {isVisible && (
                <div className="absolute z-10 w-100 rounded-md border border-gray-lighter bg-white pt-1 shadow-lg">
                    <MultiSelectPopover.Header>
                        <div className="flex flex-row space-x-2">
                            <Checkbox
                                data-testid="purchases-make-a-purchase-select-all-checkbox"
                                checked={externalSelectedItems.length === options.length}
                                onChange={handleSelectAll}
                            />
                            <span className="text-sm">Select All</span>
                            {externalSelectedItems.length > 0 && (
                                <Badge className="text-xs font-normal">{externalSelectedItems.length}</Badge>
                            )}
                        </div>
                    </MultiSelectPopover.Header>

                    <div>
                        {Object.entries(groupedOptions).map(([category, items]) => (
                            <div key={category}>
                                {category !== "default" && (
                                    <MultiSelectPopover.Header className="text-sm">
                                        {category}
                                    </MultiSelectPopover.Header>
                                )}
                                {items.map((option) => (
                                    <ProductFilterItem
                                        key={option.id}
                                        label={option.label}
                                        isChecked={externalSelectedItems.includes(option.id)}
                                        icon={option.icon}
                                        testId={option.testId}
                                        onChange={() => handleItemChange(option.id)}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
